import {
    EUROPACE_APPLICANT_STATUSES,
    EUROPACE_BANK_STATUSES,
    EUROPACE_COMMISSION_STATUSES,
    EUROPACE_FEASIBILITY_STATUS,
    EUROPACE_INSURED_RISKS,
} from 'cpool-constants';
import { z } from 'zod';

// NOTE: don't shorten this import as it would lead to a circular dependency
import { schemaEuropaceAntragstellerIdentifikation } from './Angebote/Annahme/AntragstellerIdentifikation.schema';
import { schemaBonitaetsRechnung } from './Angebote/BonitaetsRechnung.schema';
import { schemaProduktanbieter } from './Produktanbieter.schema';

/*
    https://docs.api.europace.de/privatkredit/vorgaenge/kex-vorgang-export-api/#antrag
    30.01.2026 14:45
    {
        "antragsnummer": String,
        "produktanbieterantragsnummer": String,
        "angenommenAm": "yyyy-MM-dd'T'HH:mm:ss.SSS",
        "letzteAenderungAm": "yyyy-MM-dd'T'HH:mm:ss.SSS",
        "letztesEreignisAm": "yyyy-MM-dd'T'HH:mm:ss.SSS",
        "ausgehaendigtAm": "yyyy-MM-dd'T'HH:mm:ss.SSS",
        "antragstellerstatus": {
            "status": "BEANTRAGT" | "UNTERSCHRIEBEN" | "NICHT_ANGENOMMEN" | "WIDERRUFEN",
            "letzteAenderungAm": "yyyy-MM-dd'T'HH:mm:ss.SSS"
        },
        "produktanbieterstatus": {
            "status": "NICHT_BEARBEITET" | "UNTERSCHRIEBEN" | "ABGELEHNT" | "AUTOMATISCH_ABGELEHNT" | "ZURUECKGESTELLT",
            "letzteAenderungAm": "yyyy-MM-dd'T'HH:mm:ss.SSS",
            "kommentar": String,
            "hinweise": [String]
        },
        "provisionsforderungsstatus": {
            "status": "VOLLSTAENDIG_AUSGEZAHLT",
            "letzteAenderungAm": "yyyy-MM-dd'T'HH:mm:ss.SSS"
        },
        "produkttyp": String,
        "ratenschutz": Ratenschutz,
        "gesamtkonditionen": Gesamtkonditionen,
        "ratenkredit": Ratenkredit,
        "benoetigteUnterlagen" : [BenoetigteUnterlage],
        "dokumente" : [Dokument],
        "identifikationAntragsteller1" : Identifikation,
        "identifikationAntragsteller2" : Identifikation,
        "machbarkeit": {
            "status": "MACHBAR" | "MACHBAR_UNTER_VORBEHALT" | "NICHT_MACHBAR"
        },
        "bonitaetsrechnung": Bonitaetsrechnung,
        "bausparvertrag": Bausparprodukt
    }
 */

export const schemaEuropaceAntrag_Export = z.object({
    // yyyy-MM-dd'T'HH:mm:ss.SSS
    angenommenAm: z.string().optional(),
    antragsnummer: z.string().min(1),
    antragstellerstatus: z
        .object({
            letzteAenderungAm: z.string(),
            status: z.union([z.nativeEnum(EUROPACE_APPLICANT_STATUSES), z.string()]).optional(),
        })
        .optional(),
    // The field ausgehaendigtAm shows only the timestamp of the most recent issuing of the Antrag.
    // yyyy-MM-dd'T'HH:mm:ss.SSS
    ausgehaendigtAm: z.string().optional(),
    benoetigteUnterlagen: z
        .array(
            z.object({
                unterlage: z.string().optional(),
            }),
        )
        .optional(),
    bonitaetsrechnung: schemaBonitaetsRechnung.optional(),
    dokumente: z
        .array(
            z.object({
                name: z.string().min(1).optional(),
                url: z.string().min(1).optional(),
            }),
        )
        .optional(),
    gesamtkonditionen: z
        .object({
            auszahlungsbetrag: z.number().optional(),
            // percentage
            effektivzins: z.number().optional(),
            // Euro
            gesamtkreditbetrag: z.number().optional(),
            laufzeitInMonaten: z.number().optional(),
            // Euro
            monatlicheRate: z.number().optional(),
            // Euro
            nettokreditbetrag: z.number().optional(),
            // percentage
            sollzins: z.number().optional(),
        })
        .optional(),
    identifikationAntragsteller1: schemaEuropaceAntragstellerIdentifikation.optional(),
    identifikationAntragsteller2: schemaEuropaceAntragstellerIdentifikation.optional(),
    // yyyy-MM-dd'T'HH:mm:ss.SSS
    letzteAenderungAm: z.string().optional(),
    // yyyy-MM-dd'T'HH:mm:ss.SSS
    letztesEreignisAm: z.string().optional(),
    machbarkeit: z
        .object({
            status: z.union([z.nativeEnum(EUROPACE_FEASIBILITY_STATUS), z.string()]),
        })
        .optional(),
    produktanbieterantragsnummer: z.string().optional(),
    produktanbieterstatus: z
        .object({
            hinweise: z.array(z.string()).optional(),
            kommentar: z.string().optional(),
            letzteAenderungAm: z.string(),
            status: z.union([z.nativeEnum(EUROPACE_BANK_STATUSES), z.string()]).optional(),
        })
        .optional(),
    // for some reason the docs define this property as String but add two literals as possible values
    produkttyp: z
        .union([z.literal('RATENKREDIT'), z.literal('BAUSPARKASSE_MODERNISIERUNGSKREDIT'), z.string()])
        .optional(),

    provisionsforderungsstatus: z
        .object({
            letzteAenderungAm: z.string().optional(),
            status: z.union([z.nativeEnum(EUROPACE_COMMISSION_STATUSES), z.string()]).optional(),
        })
        .optional(),
    ratenkredit: z
        .object({
            produktanbieter: schemaProduktanbieter.optional(),
            produktanbieterId: z.string().optional(),
            produktart: z.string().optional(),
            produktbezeichnung: z.string().optional(),
            schlussrate: z.number().optional(),
            vorlaufzinsenProTag: z.number().optional(),
        })
        .optional(),
    ratenschutz: z
        .object({
            praemieBrutto: z.number().optional(),
            praemieMonatlich: z.number().optional(),
            praemieNetto: z.number().optional(),
            produktanbieter: z.array(schemaProduktanbieter).optional(),
            versicherteRate: z.number().optional(),
            versicherteRisikenAntragsteller1: z
                .array(z.union([z.nativeEnum(EUROPACE_INSURED_RISKS), z.string()]))
                .optional(),
            versicherteRisikenAntragsteller2: z
                .array(z.union([z.nativeEnum(EUROPACE_INSURED_RISKS), z.string()]))
                .optional(),
            versicherungssumme: z.number().optional(),
        })
        .optional(),
});

export type SchemaEuropaceAntrag_Export = z.infer<typeof schemaEuropaceAntrag_Export>;
